import type { ApiKey, DeleteApiKeyByIdResponse } from '@deque/api-key-client';
import type {
  Filters,
  TestRecordIssue,
  UniversalIssueWithScreenshot
} from '@deque/axe-devtools-issues-utils';
import type { MetricEvent, v2 } from '@deque/billing-service-client';
import type { ProductSlugs } from '@deque/billing-utils';
import type { SafeVNode } from '@deque/groot';
import type {
  IssueDescriptionData,
  Sections as RuleHelpSections
} from '@deque/igt-rule-help';
import type {
  Project as JazzbandProject,
  JazzbandServerInfo,
  MobileAxeResult,
  MobileSettings,
  ProjectMembership,
  Session,
  SessionComparisonMetadata,
  SessionOverview,
  UserIdRolePair,
  V3Types,
  WatcherConfig
} from '@deque/jazzband-client';
import type * as jazzbandUtils from '@deque/jazzband-utils';
import { encodeURI } from '@deque/jazzband-utils';
import type {
  GeneralSettingsSchema,
  SettingsSchemaAxeDevtoolsWatcher
} from '@deque/orgwide-settings-client';
import {
  type Paginated,
  Headers as PaginationHeaders,
  setPagination
} from '@deque/pagination-utils';
import type {
  Configuration,
  ConstrainingPath,
  Scan,
  ScanRuntimeDetails,
  GlobalConfiguration as TromboneGlobalConfiguration,
  Project as TromboneProject
} from '@deque/trombone-api-client';
import type {
  TemplateMappingSchemaV1,
  TemplateMappingSchemaV2
} from '@deque/ui-export-jira';
import type axe from 'axe-core';
import qs from 'qs';
import removeTrailingSlash from 'remove-trailing-slash';
import { validate as isValidUUID } from 'uuid';
import type { FeatureFlag } from './contexts/featureFlags';

import type { AxeResults } from 'axe-core';
import type { OnboardingForm } from '~/app/components/OnboardingForm';
import { type Impacts } from '~/app/types';
import {
  type AuditorTest,
  type TestsFilterParams
} from '../axe-auditor/types/AuditorTestList';
import type { TestDetail } from '../axe-auditor/types/TestDetail';
import type {
  RunUnit,
  TestRunUnitFilterParams
} from '../axe-auditor/types/TestRunUnit';
import { type SupportedIntegrationProductSlugs } from './constants';
import type { ToastType } from './contexts/globalToast';
import type { GuidedTool } from './utils/get-guides';
import parsePasswordRequirements, {
  type PasswordRequirements
} from './utils/parsePasswordRequirements';

export type CreateApiKeyBody = Omit<
  ApiKey,
  | 'id'
  | 'api_key'
  | 'user_id'
  | 'enterprise_id'
  | 'created_at'
  | 'updated_at'
  | 'enterprise_name'
  | 'user_email'
  | 'platform'
> & { platform?: string };

export type UpdateApiKeyBody = Partial<
  Omit<ApiKey, 'id' | 'user_id' | 'enterprise_id' | 'created_at' | 'updated_at'>
>;

export interface PaginationOptions {
  page?: number;
  per_page?: number;
}

interface RequestParams extends PaginationOptions {
  method?: string;
  endpoint: string;
  token?: string;
  raw?: boolean;
  headers?: Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  // Aborts the in-flight request. Forwarded straight to `fetch`, so callers
  // can implement client-side timeouts or cancel a request when the user
  // navigates away (e.g. the mobile JSON export's 30s timeout).
  signal?: AbortSignal;
}

export interface SSOConfig {
  url: string;
  realm: string;
  publicClientId: string;
  mcpClientId: string | null;
}

interface RawRealmInfo {
  passwordPolicy: string[];
  identityProviders: string[];
}

export interface RealmInfo {
  passwordPolicy: PasswordRequirements;
  identityProviders: string[];
}

export interface User {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email: string;
  data_gather: boolean;
  hasPublicClientRole: boolean;
  hasAccountEntry: boolean;
  token: string;
  company?: string;
  roles: string[];
  groups: string[];
}
export interface SignedUpUser {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email: string;
  company?: string;
}

export interface Account {
  user_id: string;
  email: string | null;
  agreed_at: string | null;
}

export interface ProjectContributorApiKey {
  id: string;
  name: string;
}

export interface ProjectContributorData {
  user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  is_project_member: boolean;
  api_keys: ProjectContributorApiKey[];
}

interface SignUpUser {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  isMobileReadOnly?: boolean;
  isMobileFreeTrial?: boolean;
  redirectURI?: string;
  // Usage-based-outreach consent captured from the self-serve sign-up form's
  // consent checkbox. Not persisted server-side yet — see comment in
  // service/src/routes/users/sign-up.ts.
  marketingConsent?: boolean;
  product_slug?: ProductSlugs;
}

interface CreateAccountParams {
  firstName: string;
  lastName: string;
  company: string;
}

interface UpdateAccountParams {
  company?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface UpdatePasswordParams {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

export interface Team {
  id?: string;
  name: string;
  user_ids: string[];

  user_ids_to_add: string[];
  user_ids_to_remove: string[];
}

export interface RuleHelp extends RuleHelpSections {
  issueDescriptionData: IssueDescriptionData;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Issue {
  // This allows us to make assumptions about the shape of the issue for issue mapping.
  [key: string]: unknown;
  id: string;
  test_id: string;
  test_name: string;
  test_url: string | null;
  user_id: string;
  manifest_id: string | null;
  manifest_scope: string | null;
  manifest_key: string | null;
  manifest_index: number | null;
  summary: string;
  description: string | null;
  help_url: string;
  rule: string;
  source: string | null;
  created_at: string;
  updated_at: Date | null;
  impact: 'blocker' | 'critical' | 'minor' | 'moderate' | 'serious';
  help: string;
  is_manual: boolean;
  selector: string[] | null;
  pseudo: string | null;
  manifest_guide: string | null;
  variant: 'best-practice' | 'pass' | 'violation' | null;
  needs_review: boolean | null;
  remediation: Record<string, unknown> | null;
  related_nodes: string[] | null;
  shared_with: string | null;
  tags: string[] | null;
  screenshot_id: string | null;
  bounding_box: BoundingBox | null;
  integration_url: string | null;
  general_settings: Partial<GeneralSettingsSchema> | null;
}

export interface IssueToIntegrationSend extends UniversalIssueWithScreenshot {
  issueId: string;
  shareURL: string;
  fingerprint?: string;
}

type TestMetadata = Record<string, unknown> & {
  scope?: SafeVNode;
};

export interface Test {
  id: string;
  creator_id: string;
  created_at: string;
  updated_at: string | null;
  auto_tests_at: string | null;
  manual_tests_completed_at: Date | null;
  name: string;
  url: string;
  skipped_guides: string[] | null;
  metadata: TestMetadata | null;
  shared_with: string | null;
}

type Step = {
  text?: string;
  status?: string;
  [key: string]: unknown;
};

export interface UserFlow {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string | null;
  user_email?: string | null;
  axe_core_version?: string;
  data_source_version?: string | null;
  shared_with: string | null;
}

export interface UserFlowPageStateMetaData {
  page_state_id: string;
  page_state_name: string;
}

export type UserFlowResults = AxeWatcherSessionComparisonResponse;

export interface Manifest {
  id: string;
  body: {
    metadata?: {
      username?: string;
      duration?: number;
      issues?: Record<string, unknown>[];
      progress?: Step[];
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  search: unknown | null;
  created_at: Date | null;
  test_id: string;
  guide: GuidedTool | null;
  updated_at: Date | null;
  name: string | null;
}

export type Manifests = Record<GuidedTool, Manifest[]>;

export interface ScreenshotMetadata {
  width: number;
  height: number;
  format?: string;
  channels?: number;
  premultiplied?: boolean;
  size?: number;
}

export interface IntegrationTemplate {
  id: string;
  enterprise_id: string;
  product_slug: string;
  project_id: string | null;
  issue_type_id: string | null;
  name: string;
  field_mappings: TemplateMappingSchemaV1 | TemplateMappingSchemaV2 | null;
  impact_mappings: Partial<Record<Impacts, string>> | null;
  custom_labels: string[];
  integration_connection_id: string;
  attach_screenshot: boolean;
  summary_prefix: string | null;
  created_by: string | null;
  created_by_first_name: string;
  created_by_last_name: string;
  created_at: string;
  updated_by: string | null;
  updated_by_first_name: string | null;
  updated_by_last_name: string | null;
  updated_at: string | null;
}

export interface IssueMetadata {
  id: string;
  issue_id: string;
  fingerprint: string | null;
  group_id: string;
  product_source: string;
  integration_connection_id: string;
  integration_project_id: string;
  integration_issue_id: string;
  integration_url: string;
  created_by_user_id: string;
  enterprise_id: string;
  created_at: Date;
  updated_at: Date;
}

export interface ServerInfo {
  jazzband: {
    isEnabled: boolean;
    clientVersion?: string;
    serverInfo?: JazzbandServerInfo | 'unavailable';
  };
  billingServiceEnabled: boolean;
  billingService?: {
    isEnabled: boolean;
    version?: string;
  };
  mailerEnabled: boolean;
  isOnPrem: boolean;
  /** Whether AI credits are metered and enforced. Independent of `isOnPrem`. */
  aiCreditsEnabled?: boolean;
  isSelfProvisioningEnabled: boolean;
  mlServiceEnabled: boolean;
  screenshotsEnabled: boolean;
  version?: string;
  productInfo?: {
    [key in ProductSlugs]?: {
      [key: string]: unknown;
    };
  };
}
/**
 * Feature flag coming from the database (not the user's cookies).
 *
 * NOTE: This type should _only_ be used by /internal/features/admin.
 */

export interface AdminFeatureFlag {
  id: string;
  default_state: boolean;
  description: string;
  product_name: string;
  created_at: Date;
  created_by: string;
  updated_at: Date | null;
  updated_by: string | null;
  overridden: boolean;
  initial_state: boolean;
  db_overridden_created_at: Date | null;
  db_overridden_created_by: string | null;
}

/**
 * An enterprise-specific override for a feature flag.
 */
export interface EnterpriseFeatureFlagOverride {
  id: string;
  feature_id: string;
  enterprise_id: string;
  state: boolean;
  created_by: string | null;
  created_at: Date;
  updated_at: Date | null;
  updated_by: string | null;
  deleted_at: Date | null;
  deleted_by: string | null;
}

export type AICreditsTargetType = 'user' | 'enterprise';

export interface AdminAICredits {
  target_type: AICreditsTargetType;
  target_id: string;
  metric_id: string;
  used: number;
  limit: number;
}

export interface Subscription {
  email: string;
  status: 'email-sent' | 'active';
  lastUpdated: string;
}

export type WatcherNode = Omit<
  axe.NodeResult,
  'xpath' | 'ancestry' | 'any' | 'all' | 'none' | 'element'
>;

export type WatcherViolation = Omit<axe.Result, 'nodes'> & {
  nodes: WatcherNode[];
};

export interface WatcherResult {
  id: string;
  created_at: Date;
  page_state: string | null;
  url: string;
  session_id: string;
  browser_id: string;
  violations: WatcherViolation[];
}

interface Broadcast {
  message?: string;
  level?: ToastType;
  isInMaintenanceMode: boolean;
}

interface InvoiceRequestBody {
  product_name: string;
  quantity: number;
  special_request?: string;
  first_name: string;
  last_name: string;
  email: string;
  org_name: string;
  org_email: string;
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  po_number?: string;
  vat_id?: string;
}

interface InvoiceRequestResponse {
  sent: boolean;
}

type CreateProjectRequestBody = Omit<
  TromboneProject,
  | 'id'
  | 'configuration_id'
  | 'created_at'
  | 'updated_at'
  | 'user_id'
  | 'enterprise_id'
  | 'deleted_at'
  | 'is_deleted'
> & {
  configuration: Omit<
    Configuration,
    'id' | 'maximum_browsers' | 'created_at' | 'updated_at'
  > & {
    constraining_path: Omit<ConstrainingPath, 'id' | 'configuration_id'>[];
  };
};

type UpdateProjectConfigurationBody = Pick<TromboneProject, 'name'> & {
  configuration: Omit<
    Configuration,
    'id' | 'maximum_browsers' | 'created_at' | 'updated_at'
  > & {
    constraining_path: Omit<ConstrainingPath, 'id' | 'configuration_id'>[];
  };
};

type CreateProjectResponseBody = TromboneProject;

type ProjectConfiguration = TromboneProject & {
  configuration: Configuration & {
    constraining_path: Omit<ConstrainingPath, 'id' | 'configuration_id'>[];
  };
};
export type ProjectScan = TromboneProject & { scan: Scan | null };
export type ProjectScanDetails = Scan & { session: Session };
export type ProjectScanSessionComparisonMetadata = SessionComparisonMetadata & {
  scan_details_new: Scan;
  scan_details_old: Scan;
};

export interface DquTransactionNotificationParams {
  newSubscription: boolean;
  priceId?: string;
  quantity: number;
}

interface AxeWatcherSessionComparisonResponse {
  overview: jazzbandUtils.DedupedOverview;
  results: jazzbandUtils.DedupedResult[];
  pageStateResults: jazzbandUtils.DedupedPageStateResult[];
}

export type MinimalFeatureFlag = Pick<FeatureFlag, 'id' | 'state'>;

export type HttpError = Error & {
  status?: number;
  error_code?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: any;
  /** Seconds from a 429 `Retry-After`, when it carries delay-seconds. */
  retryAfterSeconds?: number;
};

/**
 * RFC 9110 delay-seconds is `1*DIGIT`; the width bound also rejects values so
 * large they overflow a 32-bit `setTimeout` delay and fire immediately. Mirrors
 * `@deque/jazzband-client`, whose own copy cannot be imported here without
 * pulling its Node-only transport into the browser bundle.
 *
 * @see /docs/lambda-queue-backpressure.md
 */
const parseRetryAfterSeconds = (header: string | null): number | undefined => {
  if (!header || !/^\d{1,5}$/.test(header)) {
    return undefined;
  }

  const seconds = Number(header);
  return seconds > 0 ? seconds : undefined;
};

/**
 * Small utility to replace certain encoding from query parameters to make the strings more readable.
 *
 * @param query - a stringified query parameter
 * @returns a stringified query parameter with %5B and %5D replaced with [ and ]
 * @example
 * ```js
 * 'customer=foo%5Bbar%5D' becomes 'customer=foo[bar]'
 * ```
 * source: {@link https://github.com/stripe/stripe-node/blob/master/src/utils.ts#L40-L52 stripe-node's utils}

 */

export const encodeQueryParams = (data: Record<string, unknown>): string => {
  return qs.stringify(data).replace(/%5B/g, '[').replace(/%5D/g, ']');
};

/**
 * Note: This method should not be called directly outside of the api client libraries.
 * Instead, you should use the methods provided by the api client libraries.
 */
export const apiRequest = async <T>(
  options: RequestParams
): Promise<T | Paginated<T>> => {
  const {
    method = 'GET',
    raw,
    endpoint,
    token,
    body,
    headers = {},
    page,
    per_page,
    signal
  } = options;

  // Ensure the endpoint starts with `/`.
  // This allows us to easily join URLs when `$API_SERVER_URL` is set.
  /* istanbul ignore if */
  if (process.env.NODE_ENV === 'development') {
    if (endpoint[0] !== '/') {
      throw new Error('Endpoint must begin with "/"');
    }
  }

  if (page !== undefined) {
    headers[PaginationHeaders.Page] = page.toString();
  }

  if (per_page !== undefined) {
    headers[PaginationHeaders.PerPage] = per_page.toString();
  }

  if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (!headers.Accept) {
    headers.Accept = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const host = removeTrailingSlash(window.appConfig.API_SERVER_URL || '');
  const res = await fetch(`${host}${endpoint}`, {
    method,
    headers,
    body: raw ? body : JSON.stringify(body),
    // We need to include cookies in all requests (for feature flags).
    credentials: 'include',
    signal
  });

  if (raw) {
    if (res.ok) {
      return res as unknown as T;
    }
    // A `raw` caller never sees the response, so carry what it needs on the
    // error: the status, plus `error_code` from the body, since a route can
    // answer the same status for unrelated reasons. Reading the body is
    // best-effort — an unparseable one must not mask the original failure.
    const error = new Error(
      `unexpected server error ${res.status}`
    ) as HttpError;
    error.status = res.status;
    try {
      const errorBody = await res.json();
      if (typeof errorBody?.error_code === 'string') {
        error.error_code = errorBody.error_code;
      }
    } catch {
      // No parseable body; `status` remains the only signal.
    }
    if (res.status === 429) {
      error.retryAfterSeconds = parseRetryAfterSeconds(
        res.headers.get('Retry-After')
      );
    }
    throw error;
  }

  // 204 No Content has no body — return undefined. Callers expecting no
  // response (e.g. Promise<void>) will receive undefined which satisfies void.
  if (res.status === 204) {
    return undefined as unknown as T;
  }

  const type = res.headers.get('Content-Type');
  if (!type || !type.includes('json')) {
    // For whatever reason, the API didn't return JSON. We don't know what to do here, so log (and throw) an error.
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('API (%s) returned non-JSON response:', endpoint, res);
    }

    throw new Error('Unexpected result from API');
  }

  const responseBody = await res.json();

  if (res.ok) {
    const rawPageHeader = res.headers.get(PaginationHeaders.Page);
    const rawPerPageHeader = res.headers.get(PaginationHeaders.PerPage);
    const rawTotalHeader = res.headers.get(PaginationHeaders.Total);
    const pageHeader = rawPageHeader ? parseInt(rawPageHeader, 10) : undefined;
    const perPageHeader = rawPerPageHeader
      ? parseInt(rawPerPageHeader, 10)
      : undefined;
    const totalHeader = rawTotalHeader
      ? parseInt(rawTotalHeader, 10)
      : undefined;

    if (
      pageHeader !== undefined &&
      perPageHeader !== undefined &&
      totalHeader !== undefined
    ) {
      return setPagination(responseBody, {
        page: pageHeader,
        per_page: perPageHeader,
        total: totalHeader
      });
    }

    return responseBody;
  }

  let errorMessage: string;
  if (responseBody.validationErrors) {
    // The signup API (POST /api/users) returns `.validationErrors` in addition to `.error`.
    // Take the first validation error.
    // TODO: use something like npmjs.org/aggregate-error and display all validation errors.
    errorMessage = Object.values(responseBody.validationErrors)[0] as string;
  } else if (responseBody.error) {
    // The API error handler returns the shape `{ error: string }`.
    errorMessage = responseBody.error;
  } else {
    // Throw a generic error if there was an issue with the error handler.
    errorMessage = 'Unexpected server error';
  }

  const error = new Error(errorMessage);
  (error as HttpError).status = res.status;
  (error as HttpError).error_code = responseBody.error_code;
  (error as HttpError).details = responseBody.details;
  if (res.status === 429) {
    (error as HttpError).retryAfterSeconds = parseRetryAfterSeconds(
      res.headers.get('Retry-After')
    );
  }

  throw error;
};

export const getUser = (token: string): Promise<User> => {
  return apiRequest({
    endpoint: '/api/logged-in',
    token
  });
};

export const getSSOConfig = (): Promise<SSOConfig> => {
  return apiRequest({
    endpoint: '/api/sso-config'
  });
};

export const getRealmInfo = (): Promise<RealmInfo> => {
  return apiRequest<RawRealmInfo>({
    endpoint: '/api/realm-info'
  }).then(realmInfo => {
    // Convert the password policy into a useable data structure.
    const strings = realmInfo.passwordPolicy;
    const requirements = parsePasswordRequirements(strings);
    return {
      ...realmInfo,
      passwordPolicy: requirements
    };
  });
};

export const signUp = (body: SignUpUser): Promise<SignedUpUser> => {
  return apiRequest({
    method: 'POST',
    endpoint: '/api/users',
    body
  });
};

export interface InvitationMetadata {
  email: string;
  enterpriseName: string;
  enterpriseId: string;
  invitationId: string;
}

export const getInvitationMetadata = (
  metadata: string
): Promise<InvitationMetadata> => {
  return apiRequest({
    endpoint: `/api/invitation-metadata?metadata=${encodeURIComponent(metadata)}`
  });
};

interface InvitationSignupBody {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  metadata: string;
  termsAccepted: boolean;
}

export interface InvitationSignupResult {
  user: SignedUpUser;
  tokens: {
    access_token: string;
    refresh_token: string;
    id_token: string;
  } | null;
  signInRequired?: boolean;
}

export const invitationSignup = (
  body: InvitationSignupBody
): Promise<InvitationSignupResult> => {
  return apiRequest({
    method: 'POST',
    endpoint: '/api/invitation-signup',
    body
  });
};

export interface ProgrammaticSessionTokens {
  access_token: string;
  refresh_token: string;
  id_token: string;
}

/**
 * GET /api/programmatic-session
 *
 * Returns tokens previously stored in an HttpOnly cookie by /api/invitation-signup.
 * Resolves to null when no cookie is present (server returns 204).
 */
export const getProgrammaticSession =
  async (): Promise<ProgrammaticSessionTokens | null> => {
    const result = await apiRequest<{
      tokens: ProgrammaticSessionTokens;
    }>({
      endpoint: '/api/programmatic-session'
    });
    if (!result || !('tokens' in result)) {
      return null;
    }
    return result.tokens;
  };

export const clearProgrammaticSession = (): Promise<void> => {
  return apiRequest({
    method: 'DELETE',
    endpoint: '/api/programmatic-session'
  });
};

export const createAccount = (
  userId: string,
  body: CreateAccountParams,
  token: string
): Promise<Account> => {
  return apiRequest({
    method: 'POST',
    endpoint: `/api/users/${userId}/accounts`,
    body,
    token
  });
};

export const updateAccount = async (
  userId: string,
  body: UpdateAccountParams,
  token: string
): Promise<SignedUpUser> => {
  const result = await apiRequest<SignedUpUser>({
    method: 'PUT',
    endpoint: `/api/users/${userId}/accounts`,
    body,
    token
  });

  return result;
};

export const updatePassword = (
  userId: string,
  body: UpdatePasswordParams,
  token: string
): Promise<void> => {
  return apiRequest({
    method: 'PUT',
    endpoint: `/api/users/${userId}/password`,
    body,
    token
  });
};

export const getRuleHelp = (
  language: string,
  ruleId: string
): Promise<RuleHelp> => {
  return apiRequest({
    method: 'GET',
    endpoint: `/api/rule-help/${language}/${ruleId}`
  });
};

export const getServerInfo = (): Promise<ServerInfo> => {
  return apiRequest({
    endpoint: '/api/internal/server-info'
  });
};

/**
 * Get the active user's feature flag states.
 */

export const getFeatureFlags = async (
  token?: string
): Promise<FeatureFlag[]> => {
  const result = await apiRequest<{ features: FeatureFlag[] }>({
    endpoint: '/api/internal/features',
    token
  });
  return result.features;
};

/**
 * Update the active user's feature flag states.
 */

export const updateFeatureFlags = (
  token: string,
  features: MinimalFeatureFlag[]
): Promise<{ success: boolean }> => {
  return apiRequest({
    endpoint: '/api/internal/features',
    method: 'POST',
    body: { features },
    token
  });
};

/**
 * Send an analytics event for server consumption. This can be used for
 * HubSpot events, or events that shouldn't be sent from a browser for
 * security purposes.
 */

type UpdateBehavior = 'NONE' | 'REPLACE' | 'INCREMENT';
export const analyticsEvent = (
  token: string,
  name: string,
  value?: string | boolean,
  updateBehavior?: UpdateBehavior
): Promise<unknown> =>
  apiRequest({
    method: 'POST',
    endpoint: '/api/analytics',
    token,
    body: {
      event_name: name,
      event_value: value,
      update_behavior: updateBehavior
    }
  }).catch(() => {
    // Noop, user shouldn't be blocked if this request fails
  });

/**
 * Get an issue by its `id`.
 *
 * @param id
 * @param token
 */

export const getIssueById = (
  id: string,
  token?: string
): Promise<TestRecordIssue> => {
  return apiRequest<TestRecordIssue>({
    method: 'GET',
    endpoint: `/api/issues/${id}`,
    token
  });
};

/**
 * Get a test by its `id`.
 */
export const getTest = (id: string, token?: string): Promise<Test> => {
  return apiRequest<Test>({
    method: 'GET',
    endpoint: `/api/tests/${id}`,
    token
  });
};

/**
 * Get a user flow by its `id`.
 */
export const getUserFlow = (id: string, token?: string): Promise<UserFlow> => {
  return apiRequest<UserFlow>({
    method: 'GET',
    endpoint: `/api/axe-devtools-pro/user-flows/${id}`,
    token
  });
};

/**
 * Update a user flow by its `id`.
 */
export const updateUserFlow = (
  id: string,
  token: string,
  updates: Partial<Pick<UserFlow, 'name' | 'shared_with'>>
): Promise<UserFlow> => {
  return apiRequest<UserFlow>({
    method: 'PUT',
    endpoint: `/api/axe-devtools-pro/user-flows/${id}`,
    token,
    body: updates
  });
};

/**
 * Delete a user flow by its `id`.
 */
export const deleteUserFlow = (id: string, token?: string): Promise<void> => {
  return apiRequest({
    method: 'DELETE',
    endpoint: `/api/axe-devtools-pro/user-flows/${id}`,
    token
  });
};

/**
 * Get user flow results by its `id`.
 */
export const getUserFlowResults = (
  id: string,
  token?: string
): Promise<UserFlowResults> => {
  return apiRequest<UserFlowResults>({
    method: 'GET',
    endpoint: `/api/axe-devtools-pro/user-flows/${id}/results`,
    token
  });
};

/**
 * Get user flow page state metadata by user flow `id`.
 * @param id
 * @param token
 */
export const getUserFlowPageStateMetaData = (
  id: string,
  token?: string
): Promise<UserFlowPageStateMetaData[]> => {
  return apiRequest<UserFlowPageStateMetaData[]>({
    method: 'GET',
    endpoint: `/api/axe-devtools-pro/user-flows/${id}/page-state-metadata`,
    token
  });
};

/**
 * Gets the issues for a test.
 */
export const getTestIssues = (
  id: string,
  token?: string
): Promise<TestRecordIssue[]> => {
  return apiRequest<TestRecordIssue[]>({
    method: 'GET',
    endpoint: `/api/tests/${id}/issues`,
    token
  });
};

/**
 * Gets the manifests for a test.
 */
export const getTestManifests = (
  id: string,
  token?: string
): Promise<Manifests> =>
  apiRequest<Manifests>({
    method: 'GET',
    endpoint: `/api/tests/${id}/manifests`,
    token
  });

/**
 * Get a screenshot's metadata by the screenshot `id`.
 *
 * @param id
 */

export const getScreenshotMetadataById = (
  id: string,
  token?: string
): Promise<ScreenshotMetadata> => {
  return apiRequest<ScreenshotMetadata>({
    method: 'GET',
    endpoint: `/api/screenshots/${id}/metadata`,
    token
  });
};

/**
 * Get a screenshot by the screenshot `id`.
 *
 * @param id
 */

export const getScreenshotById = async (
  id: string,
  token?: string
): Promise<Blob> => {
  const response = await apiRequest<Response>({
    method: 'GET',
    raw: true,
    headers: {
      Accept: 'image/png'
    },
    endpoint: `/api/screenshots/${id}`,
    token
  });
  return response.blob();
};

/**
 * Get the list of _admin_ feature flags.
 */

export const getAdminFeatureFlags = (
  token: string
): Promise<AdminFeatureFlag[]> => {
  return apiRequest({
    method: 'GET',
    endpoint: '/api/internal/admin/features',
    token
  });
};

/**
 * Update an _admin_ feature flag's `default_state` by its `id`.
 */

type UpdateAdminFeatureFlagParamType = {
  token: string;
  featureId: string;
  defaultState: boolean;
};

export const updateAdminFeatureFlag = (
  params: UpdateAdminFeatureFlagParamType
): Promise<AdminFeatureFlag> => {
  const { token, featureId, defaultState } = params;
  return apiRequest({
    method: 'PUT',
    endpoint: `/api/internal/admin/features/${featureId}`,
    token,
    body: {
      default_state: defaultState
    }
  });
};

type DeleteAdminFeatureFlagParamType = {
  token: string;
  featureId: string;
};
export const deleteAdminFeatureFlag = ({
  featureId,
  token
}: DeleteAdminFeatureFlagParamType): Promise<void> => {
  return apiRequest({
    method: 'DELETE',
    endpoint: `/api/internal/admin/features/${featureId}`,
    token
  });
};

export const getAdminAICredits = (params: {
  token: string;
  targetType: AICreditsTargetType;
  targetId: string;
}): Promise<AdminAICredits> => {
  const query = new URLSearchParams({
    target_type: params.targetType,
    target_id: params.targetId
  });
  return apiRequest({
    method: 'GET',
    endpoint: `/api/internal/admin/ai-credits?${query.toString()}`,
    token: params.token
  });
};

export const updateAdminAICredits = (params: {
  token: string;
  targetType: AICreditsTargetType;
  targetId: string;
  usedCredits: number;
}): Promise<AdminAICredits> => {
  return apiRequest({
    method: 'PUT',
    endpoint: '/api/internal/admin/ai-credits',
    token: params.token,
    body: {
      target_type: params.targetType,
      target_id: params.targetId,
      used_credits: params.usedCredits
    }
  });
};

/**
 * In-product announcements (admin). See dequelabs/walnut#9977.
 */

// The value arrays are the single source of truth; the unions are derived from
// them so adding a severity/audience is a compile error wherever it needs handling.
export const ANNOUNCEMENT_SEVERITIES = [
  'informational',
  'warning',
  'critical'
] as const;
export type AnnouncementSeverity = (typeof ANNOUNCEMENT_SEVERITIES)[number];

export const ANNOUNCEMENT_AUDIENCES = [
  'all_saas',
  'all_private_cloud',
  'all_instances'
] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];

export type AnnouncementStatus = 'active' | 'scheduled' | 'expired';

export interface AdminAnnouncement {
  id: string;
  severity: AnnouncementSeverity;
  product_slug: string;
  audience: AnnouncementAudience;
  audience_value: string | null;
  description: string;
  link: string | null;
  link_text: string | null;
  starts_at: string;
  ends_at: string;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/**
 * A list-endpoint row: the announcement plus fields computed server-side only for
 * the list response. Kept as a separate type so reading them off a single-get
 * result is a compile error rather than a runtime surprise.
 */
export interface AdminAnnouncementListItem extends AdminAnnouncement {
  status: AnnouncementStatus;
  created_by_name: string | null;
  updated_by_name: string | null;
}

export interface AnnouncementInput {
  severity: AnnouncementSeverity;
  product_slug: string;
  audience: AnnouncementAudience;
  // `audience_value` is deliberately absent: the column is reserved for V2
  // targeting and the server rejects any non-blank value until then.
  description: string;
  link?: string | null;
  link_text?: string | null;
  starts_at: string;
  ends_at: string;
}

export const getAdminAnnouncements = (
  token: string
): Promise<AdminAnnouncementListItem[]> => {
  return apiRequest({
    method: 'GET',
    endpoint: '/api/internal/admin/announcements',
    token
  });
};

export const getAdminAnnouncementById = (
  token: string,
  id: string
): Promise<AdminAnnouncement> => {
  return apiRequest({
    method: 'GET',
    endpoint: `/api/internal/admin/announcements/${id}`,
    token
  });
};

export const createAdminAnnouncement = (params: {
  token: string;
  body: AnnouncementInput;
}): Promise<AdminAnnouncement> => {
  return apiRequest({
    method: 'POST',
    endpoint: '/api/internal/admin/announcements',
    token: params.token,
    body: params.body
  });
};

export const updateAdminAnnouncement = (params: {
  token: string;
  id: string;
  body: AnnouncementInput;
}): Promise<AdminAnnouncement> => {
  return apiRequest({
    method: 'PUT',
    endpoint: `/api/internal/admin/announcements/${params.id}`,
    token: params.token,
    body: params.body
  });
};

export const deleteAdminAnnouncement = (params: {
  token: string;
  id: string;
}): Promise<void> => {
  return apiRequest({
    method: 'DELETE',
    endpoint: `/api/internal/admin/announcements/${params.id}`,
    token: params.token
  });
};

/**
 * Public, unauthenticated announcement read for consuming products. Returns only the
 * currently-active announcements for `productSlug` on this deployment.
 */

// Mirrors the server's public announcement shape (packages/server/src/lib/announcements.ts);
// keep the two in sync if the public endpoint's response changes.
export interface PublicAnnouncement {
  id: string;
  severity: AnnouncementSeverity;
  description: string;
  link: string | null;
  linkText: string | null;
  startDate: string;
  endDate: string;
}

export const getActiveAnnouncements = async (
  productSlug: string
): Promise<PublicAnnouncement[]> => {
  const response: { announcements: PublicAnnouncement[] } = await apiRequest({
    method: 'GET',
    endpoint: `/api/v1/announcements?product=${encodeURIComponent(productSlug)}`
  });
  return response.announcements;
};

/**
 * Get a paginated, filtered list of enterprise feature flag overrides.
 */
type GetEnterpriseFeatureFlagOverridesParams = {
  token: string;
  // TODO: search by enterprise name — pending enterprise_name deep-dive task.
  // search?: string;
  feature_ids?: string[];
  page: number;
  limit: number;
  sort_by?: string;
  sort_direction?: 'ASC' | 'DESC';
};

export const getEnterpriseFeatureFlagOverrides = (
  params: GetEnterpriseFeatureFlagOverridesParams
): Promise<{ overrides: EnterpriseFeatureFlagOverride[]; total: number }> => {
  const { token, feature_ids, page, limit, sort_by, sort_direction } = params;
  const queryString = encodeQueryParams({
    ...(feature_ids?.length ? { feature_ids } : {}),
    page,
    limit,
    ...(sort_by ? { sort_by } : {}),
    ...(sort_direction ? { sort_direction } : {})
  });
  return apiRequest({
    method: 'GET',
    endpoint: `/api/internal/admin/features/enterprises?${queryString}`,
    token
  });
};

/**
 * Create enterprise feature flag overrides for one or more enterprises.
 * Creates one override record per enterprise ID.
 */
type CreateEnterpriseFeatureFlagOverrideParams = {
  token: string;
  feature_id: string;
  enterprise_ids: string[];
  state: boolean;
};

export const createEnterpriseFeatureFlagOverride = (
  params: CreateEnterpriseFeatureFlagOverrideParams
): Promise<void> => {
  const { token, feature_id, enterprise_ids, state } = params;
  return apiRequest({
    method: 'POST',
    endpoint: `/api/internal/admin/features/enterprises`,
    token,
    body: { enterprise_ids, state, feature_id }
  });
};

/**
 * Soft-delete an enterprise feature flag override by its ID.
 */
export const deleteEnterpriseFeatureFlagOverride = (params: {
  token: string;
  overrideId: string;
}): Promise<void> => {
  const { token, overrideId } = params;
  return apiRequest({
    method: 'DELETE',
    endpoint: `/api/internal/admin/features/enterprises/${overrideId}`,
    token
  });
};

type UpdateEnterpriseFeatureFlagOverrideParamType = {
  token: string;
  overrideId: string;
  state: boolean;
};

type UpdateEnterpriseFeatureFlagOverrideResult = {
  updated: EnterpriseFeatureFlagOverride;
};

/**
 * Update an enterprise feature flag override by its ID.
 */
export const updateEnterpriseFeatureFlagOverride = (
  params: UpdateEnterpriseFeatureFlagOverrideParamType
): Promise<UpdateEnterpriseFeatureFlagOverrideResult> => {
  const { token, state, overrideId } = params;
  return apiRequest({
    method: 'PUT',
    endpoint: `/api/internal/admin/features/enterprises/${overrideId}`,
    token,
    body: { state }
  });
};

/**
 * Get a list of feature flag IDs overridden for enterprises.
 */
export const getOverriddenEnterpriseFeatureIds = (
  token: string
): Promise<{ featureIds: string[] }> => {
  return apiRequest({
    method: 'GET',
    endpoint: '/api/internal/admin/features/enterprises/overridden_feature_ids',
    token
  });
};

// api-client for API Keys
export const createApiKey = async (
  token: string,
  params: CreateApiKeyBody
): Promise<ApiKey> => {
  const data: ApiKey = await apiRequest({
    method: 'POST',
    endpoint: '/api/api-keys',
    token,
    body: params
  });
  return {
    ...data,
    created_at: new Date(data.created_at),
    updated_at: data.updated_at ? new Date(data.updated_at) : null
  };
};

export const getApiKeyById = async (
  token: string,
  id: string
): Promise<ApiKey> => {
  const data: ApiKey = await apiRequest({
    endpoint: `/api/api-keys/${id}`,
    token
  });
  return {
    ...data,
    created_at: new Date(data.created_at),
    updated_at: data.updated_at ? new Date(data.updated_at) : null
  };
};
export const getApiKeysByUserToken = async (
  token: string,
  activeEnterpriseOnly?: boolean
): Promise<ApiKey[]> => {
  const data: ApiKey[] = await apiRequest({
    endpoint: `/api/api-keys${activeEnterpriseOnly ? '?activeEnterpriseOnly=true' : ''}`,
    token
  });
  return data.map(apiKey => ({
    ...apiKey,
    created_at: new Date(apiKey.created_at),
    updated_at: apiKey.updated_at ? new Date(apiKey.updated_at) : null
  }));
};

export const updateApiKeyById = async (
  token: string,
  id: string,
  updates: UpdateApiKeyBody
): Promise<ApiKey> => {
  const data: ApiKey = await apiRequest({
    method: 'PUT',
    endpoint: `/api/api-keys/${id}`,
    token,
    body: updates
  });
  return {
    ...data,
    created_at: new Date(data.created_at),
    updated_at: data.updated_at ? new Date(data.updated_at) : null
  };
};

export const deleteApiKeyById = (
  token: string,
  id: string
): Promise<DeleteApiKeyByIdResponse> =>
  apiRequest({
    method: 'DELETE',
    endpoint: `/api/api-keys/${id}`,
    token
  });

interface GetWatcherSessionOverviewsParams extends PaginationOptions {
  project_id: string;
  token: string | undefined;
  useV2?: boolean;
  session_ids?: string[];
}

export const getWatcherSessionOverviews = async ({
  project_id,
  token,
  useV2,
  session_ids = [],
  ...options
}: GetWatcherSessionOverviewsParams): Promise<Paginated<SessionOverview>> => {
  let endpoint = `/api/axe-watcher/${project_id}/sessions`;

  if (useV2) {
    const searchParams = new URLSearchParams();

    if (session_ids.length) {
      searchParams.set('session_ids', session_ids.join(','));
    }

    endpoint = `/api/axe-watcher/v2/${project_id}/sessions?${searchParams.toString()}`;
  }

  const results = await apiRequest({
    method: 'GET',
    token,
    endpoint,
    ...options
  });

  return results as Paginated<SessionOverview>;
};

export const getSessionListWithOverviews = async ({
  project_id,
  token,
  ...options
}: Omit<GetWatcherSessionOverviewsParams, 'apiKeyId'> & {
  project_id: string;
}): Promise<SessionOverview[]> => {
  const results = await apiRequest({
    method: 'GET',
    token,
    endpoint: `/api/axe-spider/${project_id}/sessions`,
    ...options
  });

  return results as SessionOverview[];
};

export interface TrialMetadata {
  duration: number;
}

export const getProductTrialMetadata = async (
  productSlug: string
): Promise<TrialMetadata> => {
  const trialMetadata: TrialMetadata = await apiRequest({
    endpoint: `/api/${productSlug}/trial-metadata`
  });

  return trialMetadata;
};

export const getBroadcast = (): Promise<Broadcast> =>
  apiRequest({
    endpoint: '/api/broadcast'
  });

/**
 * Latest-session-derived fields the axe-watcher project endpoints attach on top
 * of the base Jazzband project. Mirrors the service's `Project` type in
 * `services/axe-devtools/service/src/routes/axe-watcher/listProjects.ts`, kept
 * in sync by hand — #15884 consolidated the gitful predicate, not this response
 * shape. `has_git_information` is the service's gitful classification (the full
 * `git_url` + `git_branch` + `git_commit_sha` triple), not merely "git_url set".
 */
export interface AxeDeveloperHubProjectLatestSessionInfo {
  /* Jazzband session creation date; null when the project has no runs yet. */
  last_session_created_at: string | null; // Date
  /* Does the latest session have git information? */
  has_git_information: boolean;
  /* Jazzband session git URL (null when gitless). */
  git_url: string | null;
}

export interface AxeWatcherProject
  extends JazzbandProject,
    AxeDeveloperHubProjectLatestSessionInfo {
  /* API Key name */
  name: string;
  /* API Key ID */
  project_id: string;
  /* Is the user the owner of the API Key? */
  is_owner: boolean;
  /* Was the API key created when the user was part of an enterprise? */
  is_enterprise_project: boolean;
  /* The current user's role on the project, or null when they aren't a member. */
  membership_role: ProjectMembership['role'] | null;
}

export const getDevhubProjectContributors = (
  token: string,
  project_id: string
): Promise<ProjectContributorData[]> =>
  apiRequest({
    method: 'GET',
    endpoint: `/api/axe-watcher/${project_id}/contributors`,
    token
  });

export interface AxeWatcherSessionComparison {
  session_created_at: string; // Date
  /** User-defined display name for the current session. */
  session_new_name: string | null;
  session_branch_name: string | null;
  /** null if non-Git. */
  session_commit_sha: string | null;
  session_commit_tag: string | null;
  session_commit_message: string | null;

  previous_session_created_at: string; // Date
  /** User-defined display name for the baseline session. */
  session_old_name: string | null;
  previous_session_branch_name: string | null;
  /** null if non-Git. */
  previous_session_commit_sha: string | null;
  previous_session_commit_tag: string | null;
  previous_session_commit_message: string | null;

  results: jazzbandUtils.DedupedResult[];
  page_states: jazzbandUtils.DedupedPageStateResult[];
  summary: jazzbandUtils.DedupedOverview;
  settings: GeneralSettingsSchema | null;
  /**
   * The session's stored DevHub (watcher) configuration snapshot. Hashed on
   * load to drive the a11y-threshold drift indicator (#15122).
   */
  devhub_settings: SettingsSchemaAxeDevtoolsWatcher | null;
  watcher_config: WatcherConfig | null;
  data_source: string | null;
  data_source_version: string | null;
  total_issues: number;
  total_scans: number;
  critical_issues: number;
  serious_issues: number;
  moderate_issues: number;
  minor_issues: number;
  /** null when there are no scans to score. */
  accessibility_score: number | null;
  metadata: V3Types.MobileScanMetadata | null;
  run_by: string | null;
  /** The resolved enabled rule set captured when the session ran; null for
   * legacy-SDK sessions. */
  mobile_settings: MobileSettings | null;
  is_canonical_source: boolean | null;
}

export interface AxeWatcherResult {
  id: string;
  session_id: string;
  browser_id: string;
  page_state: string;
  results: AxeResults;
}

export interface SessionComparisonWithRunBy extends V3Types.SessionComparison {
  run_by: string | null;
}

export const getAxeDeveloperHubDefaultBranch = (
  token: string | undefined,
  project_id: string
): Promise<SessionComparisonWithRunBy> => {
  return apiRequest({
    endpoint: `/api/axe-watcher/v3/${project_id}/branches/default`,
    method: 'GET',
    token
  });
};

export const getProjectNonPipelineContributors = (
  token: string | undefined,
  project_id: string
): Promise<ProjectContributorData[]> => {
  return apiRequest({
    endpoint: `/api/axe-watcher/${project_id}/contributors`,
    method: 'GET',
    token
  });
};

export const getAxeDeveloperHubBranchesList = (
  token: string | undefined,
  project_id: string,
  created_by_user_api_key_id?: string[],
  is_canonical_source?: boolean,
  paginationOptions?: PaginationOptions
): Promise<Paginated<SessionComparisonWithRunBy[]>> => {
  const params = new URLSearchParams();

  if (created_by_user_api_key_id) {
    created_by_user_api_key_id.forEach(id => {
      params.append('created_by_user_api_key_id', id);
    });
  }

  if (typeof is_canonical_source === 'boolean') {
    params.set('is_canonical_source', String(is_canonical_source));
  }

  const queryString = params.toString();
  const endpoint = `/api/axe-watcher/v3/${project_id}/branches${
    queryString ? `?${queryString}` : ''
  }`;

  return apiRequest({
    endpoint,
    method: 'GET',
    token,
    ...paginationOptions
  }) as Promise<Paginated<SessionComparisonWithRunBy[]>>;
};

export const getAxeDeveloperHubCommitsList = (
  token: string | undefined,
  project_id: string,
  branch_name: string,
  created_by_user_api_key_id?: string[],
  is_canonical_source?: boolean,
  paginationOptions?: PaginationOptions
): Promise<Paginated<SessionComparisonWithRunBy[]>> => {
  const encodedBranchName = encodeURI(branch_name);

  if (!encodedBranchName || encodedBranchName.trim() === '') {
    throw new Error('Invalid branch name: branch name cannot be empty');
  }

  const params = new URLSearchParams();

  if (created_by_user_api_key_id) {
    created_by_user_api_key_id.forEach(id => {
      params.append('created_by_user_api_key_id', id);
    });
  }

  if (typeof is_canonical_source === 'boolean') {
    params.set('is_canonical_source', String(is_canonical_source));
  }

  const queryString = params.toString();

  const endpoint = `/api/axe-watcher/v3/${project_id}/branches/${encodedBranchName}/commits${
    queryString ? `?${queryString}` : ''
  }`;

  return apiRequest({
    endpoint,
    method: 'GET',
    token,
    ...paginationOptions
  }) as Promise<Paginated<SessionComparisonWithRunBy[]>>;
};

export const getAxeDeveloperHubSessionsBySessionIds = (
  token: string | undefined,
  project_id: string,
  session_ids: string[]
): Promise<SessionComparisonWithRunBy[]> => {
  const queryString = encodeQueryParams({
    session_ids: session_ids.join(',')
  });

  return apiRequest({
    endpoint: `/api/axe-watcher/v3/${project_id}/sessions/query?${queryString}`,
    method: 'GET',
    token
  });
};

export const fetchSessionComparisonByPairs = (
  token: string | undefined,
  project_id: string,
  session_pairs: Array<[string, string]>
): Promise<SessionComparisonWithRunBy[]> => {
  // Encode pairs as "new1:old1,new2:old2"
  const pairsString = session_pairs
    .map(([newId, oldId]) => `${newId}:${oldId}`)
    .join(',');

  const queryString = encodeQueryParams({
    session_pairs: pairsString
  });

  return apiRequest({
    endpoint: `/api/axe-watcher/v3/${project_id}/sessions_comparisons/query?${queryString}`,
    method: 'GET',
    token
  });
};

export const getAxeDeveloperHubGitlessList = (
  token: string | undefined,
  project_id: string,
  created_by_user_api_key_id?: string[],
  is_canonical_source?: boolean,
  paginationOptions?: PaginationOptions
): Promise<Paginated<SessionComparisonWithRunBy[]>> => {
  const params = new URLSearchParams();

  if (created_by_user_api_key_id) {
    created_by_user_api_key_id.forEach(id => {
      params.append('created_by_user_api_key_id', id);
    });
  }

  if (typeof is_canonical_source === 'boolean') {
    params.set('is_canonical_source', String(is_canonical_source));
  }

  const queryString = params.toString();
  const endpoint = `/api/axe-watcher/v3/${project_id}/gitless${
    queryString ? `?${queryString}` : ''
  }`;

  return apiRequest({
    endpoint,
    method: 'GET',
    token,
    ...paginationOptions
  }) as Promise<Paginated<SessionComparisonWithRunBy[]>>;
};

export const invoiceRequest = (
  data: InvoiceRequestBody
): Promise<InvoiceRequestResponse> =>
  apiRequest({
    endpoint: '/api/axe-devtools/invoice-request',
    method: 'POST',
    body: data
  });

export const updateProjectConfiguration = (
  token: string,
  project_id: string,
  data: UpdateProjectConfigurationBody
): Promise<ProjectConfiguration> =>
  apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/configuration`,
    method: 'PUT',
    body: data,
    token
  });

export const createProjectRequest = (
  token: string,
  data: CreateProjectRequestBody
): Promise<CreateProjectResponseBody | null> =>
  apiRequest({
    endpoint: `/api/axe-spider/projects`,
    method: 'POST',
    body: data,
    token
  });

export const getSpiderProjects = (token: string): Promise<ProjectScan[]> =>
  apiRequest({
    endpoint: '/api/axe-spider/projects',
    method: 'GET',
    token
  });

export const getSpiderProjectSessions = async (
  project_id: string,
  token: string,
  options?: PaginationOptions
): Promise<Paginated<SessionOverview>> => {
  const data = await apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/sessions`,
    method: 'GET',
    token,
    ...options
  });

  return data as Paginated<SessionOverview>;
};

export const getSpiderProjectScan = (
  project_id: string,
  scan_id: string,
  token: string
): Promise<Scan> =>
  apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/scans/${scan_id}`,
    method: 'GET',
    token
  });

export const getSpiderProjectConfiguration = (
  token: string,
  project_id: string
): Promise<ProjectConfiguration> =>
  apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/configuration`,
    method: 'GET',
    token
  });

export const startSpiderProject = async (
  data: TromboneGlobalConfiguration,
  token: string,
  project_id: string
): Promise<void> => {
  await apiRequest({
    body: data,
    endpoint: `/api/axe-spider/projects/${project_id}/scans`,
    method: 'POST',
    token
  });
};

export const abortSpiderProject = async (
  token: string,
  project_id: string,
  scan_id: string
): Promise<void> => {
  await apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/scans/${scan_id}`,
    method: 'DELETE',
    token
  });
};

export const getSpiderProject = async (
  project_id: string,
  token: string
): Promise<ProjectScan> => {
  const data = await apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}`,
    method: 'GET',
    token
  });

  return data as ProjectScan;
};

export const deleteSpiderProject = async (
  token: string,
  project_id: string
): Promise<void> => {
  await apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}`,
    method: 'DELETE',
    token
  });
};

export const getAxeSpiderScanDetails = (
  token: string,
  project_id: string,
  scan_id: string
): Promise<ProjectScanDetails> =>
  apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/scans/${scan_id}/details`,
    token,
    method: 'GET'
  });

export const getAxeSpiderProjectScanComparison = (
  token: string,
  project_id: string,
  scan_id: string
): Promise<AxeWatcherSessionComparisonResponse> =>
  apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/scan_comparisons/${scan_id}`,
    token,
    method: 'GET'
  });

export const getAxeSpiderProjectScanComparisonMetadata = (
  token: string,
  project_id: string,
  scan_id_new: string,
  scan_id_old: string
): Promise<ProjectScanSessionComparisonMetadata> =>
  apiRequest({
    endpoint: `/api/axe-spider/projects/${project_id}/scan_comparisons_metadata/${scan_id_new}/${scan_id_old}`,
    token,
    method: 'GET'
  });

export const getAxeSpiderProjectScanIssues = (
  token: string,
  project_id: string,
  scan_id: string,
  previous_scan_id?: string | null
): Promise<UserFlowResults> => {
  const queryParams = encodeQueryParams(
    previous_scan_id
      ? {
          previous_session_id: previous_scan_id
        }
      : {}
  );

  return apiRequest({
    endpoint:
      `/api/axe-spider/projects/${project_id}/scans/${scan_id}/issues` +
      (queryParams ? `?${queryParams}` : ''),
    token,
    method: 'GET',
    headers: {
      'Accept-Encoding': 'gzip'
    }
  });
};

export const getSpiderProjectScanRuntimeDetails = (
  token: string,
  project_id: string,
  scan_id?: string
): Promise<ScanRuntimeDetails> => {
  const queryParams = encodeQueryParams({
    scan_id
  });

  return apiRequest({
    endpoint:
      `/api/axe-spider/projects/${project_id}/runtime-details` +
      (queryParams ? `?${queryParams}` : ''),
    token,
    method: 'GET'
  });
};

export const dquTransactionNotification = async (
  token: string,
  data: DquTransactionNotificationParams
): Promise<void> => {
  await apiRequest({
    endpoint: '/api/deque-university/transaction-notification',
    method: 'POST',
    token,
    body: data
  });
};

export type SessionComparisonMetadataV3 = Omit<
  SessionComparisonMetadata,
  'comparison'
> & {
  comparison: SessionComparisonWithRunBy;
};

export const getAxeWatcherSessionComparisonMetadata = async (
  token: string | undefined,
  params: { project_id: string; session_id_new: string; session_id_old: string }
): Promise<SessionComparisonMetadataV3> => {
  const data = await apiRequest({
    endpoint: `/api/axe-watcher/${params.project_id}/session_comparisons_metadata/${params.session_id_new}/${params.session_id_old}`,
    method: 'GET',
    token
  });

  return data as SessionComparisonMetadataV3;
};

export const getAxeWatcherSessionComparison = async (
  token: string | undefined,
  params: { project_id: string; session_comparison_id: string }
): Promise<AxeWatcherSessionComparisonResponse> => {
  const data = await apiRequest({
    endpoint: `/api/axe-watcher/${params.project_id}/session_comparisons/${params.session_comparison_id}`,
    method: 'GET',
    token
  });

  return data as AxeWatcherSessionComparisonResponse;
};

export const updateDevHubSessionName = async (
  token: string | undefined,
  params: { project_id: string; session_id: string; name: string | null }
): Promise<Session> => {
  const data = await apiRequest({
    endpoint: `/api/axe-watcher/${params.project_id}/sessions/${params.session_id}`,
    method: 'PATCH',
    token,
    body: { name: params.name }
  });

  return data as Session;
};

export const getDevHubMobileRawResult = async (
  token: string | undefined,
  params: { project_id: string; result_id: string; session_id: string }
): Promise<MobileAxeResult> => {
  const data = await apiRequest<MobileAxeResult>({
    endpoint: `/api/axe-watcher/${params.project_id}/session/${params.session_id}/raw_result/mobile/${params.result_id}`,
    method: 'GET',
    token
  });

  return data;
};

export const getAxeWatcherProject = async (
  token: string | undefined,
  project_id: string
): Promise<AxeWatcherProject> => {
  const data = await apiRequest<AxeWatcherProject>({
    endpoint: `/api/axe-watcher/${project_id}/project`,
    method: 'GET',
    token
  });

  return data;
};

export const deleteAxeWatcherProject = async (
  project_id: string,
  token: string
): Promise<void> => {
  await apiRequest({
    endpoint: `/api/axe-watcher/v2/projects/${project_id}`,
    method: 'DELETE',
    token
  });
};

export const getIntegrationToken = async (
  token: string,
  enterprise_id: string
): Promise<{ token: string }> => {
  const { token: signedJwt } = await apiRequest<{ token: string }>({
    endpoint: `/api/integrations/enterprises/${enterprise_id}/token`,
    method: 'GET',
    token
  });
  return { token: signedJwt };
};

type CreateIntegrationTemplateParams = {
  product_slug: string;
  project_id: string;
  issue_type_id: string;
  name: string;
  field_mappings?: TemplateMappingSchemaV2;
  impact_mappings?: Record<string, unknown>;
  custom_labels?: Array<string>;
  attach_screenshot?: boolean;
  summary_prefix?: string;
};

export const createIntegrationTemplate = (
  token: string,
  enterprise_id: string,
  connection_id: string,
  data: CreateIntegrationTemplateParams
): Promise<IntegrationTemplate> => {
  return apiRequest<IntegrationTemplate>({
    endpoint: `/api/integrations/enterprises/${enterprise_id}/connections/${connection_id}/templates`,
    method: 'POST',
    token,
    body: data
  });
};

type UpdateIntegrationTemplateParams = Partial<
  Omit<CreateIntegrationTemplateParams, 'product_slug'>
>;

export const updateIntegrationTemplate = (
  token: string,
  enterprise_id: string,
  connection_id: string,
  template_id: string,
  data: UpdateIntegrationTemplateParams
): Promise<IntegrationTemplate> => {
  return apiRequest<IntegrationTemplate>({
    endpoint: `/api/integrations/enterprises/${enterprise_id}/connections/${connection_id}/templates/${template_id}`,
    method: 'PUT',
    token,
    body: data
  });
};

type DeleteResult = {
  deleted: boolean;
};

export const deleteIntegrationTemplate = (
  enterprise_id: string,
  connection_id: string,
  template_id: string,
  token?: string
): Promise<DeleteResult> => {
  return apiRequest<DeleteResult>({
    method: 'DELETE',
    endpoint: `/api/integrations/enterprises/${enterprise_id}/connections/${connection_id}/templates/${template_id}`,
    token
  });
};

export const getIntegrationTemplateById = (
  token: string,
  enterprise_id: string,
  connection_id: string,
  template_id: string
): Promise<IntegrationTemplate | null> => {
  return apiRequest<IntegrationTemplate | null>({
    method: 'GET',
    endpoint: `/api/integrations/enterprises/${enterprise_id}/connections/${connection_id}/templates/${template_id}`,
    token
  });
};

export const getEnterpriseIntegrationTemplates = (
  enterprise_id: string,
  connection_id: string,
  token?: string,
  product?:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration
): Promise<IntegrationTemplate[]> => {
  const queryParam = encodeQueryParams({
    product_slug: product
  });
  return apiRequest<IntegrationTemplate[]>({
    method: 'GET',
    endpoint: `/api/integrations/enterprises/${enterprise_id}/connections/${connection_id}/templates${
      queryParam ? `?${queryParam}` : ''
    }`,
    token
  });
};

type SendIssueBody = {
  template_id: string;
  group_id: string;
  product_source_slug: string;
  issues: IssueToIntegrationSend[];
  mappedIntegrationFields?: {
    key: string;
    value: unknown;
  }[];
};

export const sendIssue = (
  token: string,
  enterprise_id: string,
  connection_id: string,
  options: SendIssueBody
): Promise<IssueToIntegrationSend[]> => {
  return apiRequest<IssueToIntegrationSend[]>({
    endpoint: `/api/integrations/enterprises/${enterprise_id}/connections/${connection_id}/issues`,
    method: 'POST',
    token,
    body: options
  });
};

export const getIssueMetadataById = (
  token: string,
  enterprise_id: string,
  issue_metadata_id: string
): Promise<IssueMetadata | null> => {
  return apiRequest<IssueMetadata | null>({
    method: 'GET',
    endpoint: `/api/enterprise/${enterprise_id}/issue-metadata/${issue_metadata_id}`,
    token
  });
};
interface IssueMetadataListQueryParams {
  limit?: number;
  offset?: number;
  group_id?: string;
  issue_id?: string;
  fingerprint?: string;
  created_by_user_id?: string;
}
export const getIssueMetadataListByParams = (
  token: string,
  enterprise_id: string,
  queryParams: IssueMetadataListQueryParams = {}
): Promise<IssueMetadata[]> => {
  const encodedQueryParams = encodeQueryParams(
    queryParams as Record<string, unknown>
  );
  const queryString = encodedQueryParams ? `?${encodedQueryParams}` : '';
  return apiRequest<IssueMetadata[]>({
    method: 'GET',
    endpoint: `/api/enterprise/${enterprise_id}/issue-metadata${queryString}`,
    token
  });
};

interface GetAuditorTestsParams extends TestsFilterParams, PaginationOptions {
  token: string;
}

export const getAxeAuditorTests = async ({
  token,
  ...options
}: GetAuditorTestsParams): Promise<Paginated<AuditorTest>> => {
  const sortBy = options.sortBy
    ? options.sortBy.split(' ').join('')
    : options.sortBy;
  const queryParams = encodeQueryParams({
    sortBy,
    sortOrder: options.sortOrder,
    createdOnEndingAt: options.createdOnEndingAt,
    createdOnStartingFrom: options.createdOnStartingFrom,
    type: options.type,
    testGroup: options.testGroup,
    search: options.search
  });
  const data = await apiRequest({
    endpoint:
      '/api/axe-auditor/v1/tests' + (queryParams ? `?${queryParams}` : ''),
    method: 'GET',
    token,
    ...options
  });

  return data as Paginated<AuditorTest>;
};
interface GetAuditorTestDetailParams {
  token: string;
  testId: string;
}
export const getAuditorTestDetail = async ({
  testId,
  token
}: GetAuditorTestDetailParams): Promise<TestDetail> => {
  const data = await apiRequest({
    endpoint: '/api/axe-auditor/v1/tests/' + testId,
    method: 'GET',
    token
  });
  return data as TestDetail;
};
interface GetAuditorTestRunUnitsParams
  extends TestRunUnitFilterParams,
    PaginationOptions {
  token: string;
  testId: string;
  testRunId: string;
}
export const getAxeAuditorTestRunUnits = async ({
  token,
  testId,
  testRunId,
  ...options
}: GetAuditorTestRunUnitsParams): Promise<Paginated<RunUnit>> => {
  const sortBy = options.sortBy
    ? options.sortBy.split(' ').join('')
    : options.sortBy;
  const search = (options.search || '').trim();
  const searchParam = search ? { search } : {};
  const queryParams = encodeQueryParams({
    sortBy,
    sortOrder: options.sortOrder,
    type: options.type,
    status: options.status,
    ...searchParam
  });
  const data = await apiRequest({
    endpoint:
      `/api/axe-auditor/v1/tests/${testId}/test-runs/${testRunId}/units` +
      (queryParams ? `?${queryParams}` : ''),
    method: 'GET',
    token,
    ...options
  });
  return data as Paginated<RunUnit>;
};

/** `project_id` is absent by design: the server generates it. */
export interface CreateDeveloperHubProjectParams
  extends Omit<
    JazzbandProject,
    | 'project_id'
    | 'enterprise_id'
    | 'created_at'
    | 'updated_at'
    | 'project_settings'
  > {
  token: string;
  /** Omit to store the default for `selected_project_type`. */
  project_settings?: JazzbandProject['project_settings'];
}

export const createDeveloperHubProject = async ({
  token,
  ...body
}: CreateDeveloperHubProjectParams): Promise<JazzbandProject> => {
  const data = await apiRequest<JazzbandProject>({
    token,
    body,
    endpoint: '/api/axe-watcher/v2/projects',
    method: 'POST'
  });

  return data;
};

/**
 * Mirrors the route's partial-PATCH contract: an omitted field keeps its stored
 * value, an explicit `null` clears a language or platform. `enterprise_id` is
 * absent by design — an update must not reattribute a project.
 */
export interface UpdateDeveloperHubProjectParams
  extends Partial<
    Omit<
      JazzbandProject,
      'project_id' | 'enterprise_id' | 'created_at' | 'updated_at'
    >
  > {
  project_id: string;
  token: string;
}

export const updateDeveloperHubProject = async ({
  project_id,
  token,
  ...body
}: UpdateDeveloperHubProjectParams): Promise<JazzbandProject> => {
  // `project_id` becomes a path segment, so reject anything that is not a UUID
  // rather than letting an arbitrary value shape the request URL.
  if (!isValidUUID(project_id)) {
    throw new Error('Invalid project id: project id must be a UUID');
  }

  const data = await apiRequest<JazzbandProject>({
    token,
    body,
    endpoint: `/api/axe-watcher/v2/projects/${project_id}`,
    method: 'PATCH'
  });

  return data;
};

export interface AxeWatcherProjectDetails
  extends JazzbandProject,
    AxeDeveloperHubProjectLatestSessionInfo {
  user_id: string | null;
  membership_role: ProjectMembership['role'] | null;
}

export const getAxeWatcherProjectDetails = async (
  token: string | undefined,
  project_id: string
): Promise<AxeWatcherProjectDetails> => {
  const data = await apiRequest<AxeWatcherProjectDetails>({
    endpoint: `/api/axe-watcher/${project_id}/project`,
    method: 'GET',
    token
  });

  return data;
};

export { Session as SessionDetails };

interface GetWatcherSesssionDetailsParams {
  token?: string; // May be a shared report, so this is optional.
  project_id: string;
  session_id: string;
}

/** Get an individual Axe Watcher session's details (the Session itself, **NOT** a SessionOverview). */
export const getWatcherSessionDetails = async ({
  token,
  project_id,
  session_id
}: GetWatcherSesssionDetailsParams): Promise<Session> => {
  const data = await apiRequest<Session>({
    endpoint: `/api/axe-watcher/${project_id}/session-details/${session_id}`,
    method: 'GET',
    token
  });
  return data;
};

interface CloseWatcherSessionParams {
  token?: string; // May be a shared report, so this is optional.
  project_id: string;
  session_id: string;
}

/** Close an Axe Watcher session and all of its sources. */
export const closeWatcherSession = async ({
  token,
  project_id,
  session_id
}: CloseWatcherSessionParams): Promise<void> => {
  await apiRequest({
    endpoint: `/api/axe-watcher/${project_id}/session-details/${session_id}`,
    method: 'DELETE',
    token
  });
};

export interface SendTestInputParams {
  testId: string;
  testUnitId: string;
  overrideIssuesFlag: boolean;
  token?: string;
  enterpriseId: string | undefined;
  standard: string;
  connectionId: string | undefined;
  filters: Filters;
}

export interface SendTestResponse {
  success: boolean;
  status: number;
  message: string;
}

export const sendTest = async ({
  testId,
  testUnitId,
  overrideIssuesFlag = false,
  token,
  enterpriseId,
  standard,
  connectionId,
  filters
}: SendTestInputParams): Promise<SendTestResponse> => {
  const response = await apiRequest({
    endpoint: `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/test`,
    method: 'POST',
    body: {
      testId,
      testUnitId,
      overrideIssuesFlag,
      standard,
      filters
    },
    token
  });

  return response as SendTestResponse;
};

export const sendOnboardingForm = async (
  token: string,
  data: OnboardingForm
): Promise<void> => {
  await apiRequest({
    endpoint: '/api/onboarding-form',
    method: 'POST',
    token,
    body: data
  });
};

export const getAiCreditBalance = async (
  token: string
): Promise<v2.MetricWithLimits> => {
  const data = await apiRequest<v2.MetricWithLimits>({
    endpoint: '/api/ai-tokens/balance',
    method: 'GET',
    token
  });

  return data;
};

/** Get the subscription associated with the current user's AI credits. */
export const getAiCreditSubscription = async (
  token: string
): Promise<v2.UserSubscription | v2.EnterpriseSubscription> => {
  const data = await apiRequest<
    v2.UserSubscription | v2.EnterpriseSubscription
  >({
    endpoint: '/api/ai-tokens/subscription',
    method: 'GET',
    token
  });

  return data;
};

/**
 * Metric event with additional user information. Used for the response body of the /api/ai-tokens/usage endpoint. This
 * should match the AiTokenUsageEvent type in packages/server/src/routes/ai-tokens/types.ts.
 */
export interface AiTokenUsageEvent extends MetricEvent {
  user: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  };
}

/**
 * Returns AI token usage events that occurred in the last 60 days.
 * @param token The user's authentication token.
 * @returns A promise that resolves to an array of AiTokenUsageEvent objects.
 */
export const getAiTokenUsage = (token: string): Promise<AiTokenUsageEvent[]> =>
  apiRequest({
    endpoint: '/api/ai-tokens/usage',
    method: 'GET',
    token
  });

export interface GetEnterpriseCalendarLinkResponse {
  calendarLink: string | null;
}

/** Get the account executive calendar link for an enterprise. */
export const getEnterpriseCalendarLink = (
  token: string,
  enterprise_id: string | null
): Promise<GetEnterpriseCalendarLinkResponse> => {
  if (!enterprise_id) {
    return Promise.resolve({
      calendarLink: null
    });
  }

  return apiRequest({
    endpoint: `/api/billing/v2/enterprises/${enterprise_id}/calendar-link`,
    method: 'GET',
    token
  });
};

export interface AxeDeveloperHubProject extends JazzbandProject {
  /* Jazzband session creation date */
  last_session_created_at: Date | null;
  /* Does the session have git information? */
  has_git_information: boolean;
  /* Jazzband session git URL */
  git_url: string | null;
  /* Was the project created when the user was part of an enterprise? */
  is_enterprise_project: boolean;
  /* Name of the user who created the session */
  run_by: string | null;
  /* The latest session of the project */
  latest_session: Session | null;
}

export const getAxeDeveloperHubProjectList = (
  token: string
): Promise<AxeDeveloperHubProject[]> => {
  return apiRequest({
    endpoint: '/api/axe-watcher/v2/projects',
    method: 'GET',
    token
  });
};

export interface GetAxeDeveloperHubMembershipsResponse
  extends ProjectMembership {
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  subscriptions: string[] | null;
}
export const getAxeDeveloperHubMemberships = (
  token: string,
  project_id: string
): Promise<GetAxeDeveloperHubMembershipsResponse[]> => {
  return apiRequest({
    endpoint: `/api/axe-watcher/v1/${project_id}/memberships`,
    method: 'GET',
    token
  });
};

interface CreateAxeDeveloperHubMembershipParams {
  token: string;
  project_id: string;
  user_id_role_pairs: UserIdRolePair[];
}

export const createAxeDeveloperHubMembership = ({
  token,
  project_id,
  user_id_role_pairs
}: CreateAxeDeveloperHubMembershipParams): Promise<void> => {
  return apiRequest({
    endpoint: `/api/axe-watcher/v1/${project_id}/memberships`,
    method: 'POST',
    token,
    body: {
      user_id_role_pairs
    }
  });
};

interface DeleteAxeDeveloperHubMembershipsParams {
  token: string;
  project_id: string;
  user_ids: string[];
}

/** Delete Axe DevHub project memberships from the project. */
export const deleteAxeDeveloperHubMemberships = async ({
  token,
  project_id,
  user_ids
}: DeleteAxeDeveloperHubMembershipsParams): Promise<void> => {
  const queryParams = new URLSearchParams();
  queryParams.set('user_ids', user_ids.join(','));

  await apiRequest({
    method: 'DELETE',
    endpoint: `/api/axe-watcher/v1/${project_id}/memberships?${queryParams.toString()}`,
    token
  });
};

interface UpdateAxeDeveloperHubMembershipParams {
  token: string;
  project_id: string;
  user_id_role_pairs: UserIdRolePair[];
}

export const updateAxeDeveloperHubMembership = ({
  token,
  project_id,
  user_id_role_pairs
}: UpdateAxeDeveloperHubMembershipParams): Promise<void> => {
  return apiRequest({
    endpoint: `/api/axe-watcher/v1/${project_id}/memberships`,
    method: 'PATCH',
    token,
    body: {
      user_id_role_pairs
    }
  });
};
